import React from "react";
import { ShieldCheck } from "lucide-react";
import Notice from "../common/Notice";
import {
  EARLY_EXIT_DISCLOSURE,
  STAKE_RISK_DISCLOSURE,
  XP_DISCLAIMER,
} from "../../config/supernovaXp";
import { CONFIRM_RISK_SENTENCE } from "./supernovaSignatureRequest";

/**
 * Compliance surfaces for the Supernova tab (FIRE brief §6, education-first).
 *
 * The banner renders wherever a stake or unstake action is COMMITTED — the
 * stake and unstake modals. The v4 redesign took it off the top of the tab
 * itself, where it sat above the fold ahead of anything to act on; its copy did
 * not go anywhere, it moved into the footer below.
 *
 * Required copy, sourced from config/supernovaXp.js so the tab and the engine
 * quote the same words: do not soften it, do not paraphrase it here, and never
 * add a return, income, yield, or dollar figure to this tab.
 */

/**
 * Same framing order as the Strategy Automation banner (ARC-38): the custody
 * headline leads, the risk clause follows it in the body. "High risk" moved out
 * of the headline and into the first body sentence — it did not leave the
 * screen, and the sourced disclosures below it are untouched and verbatim.
 */
export const StakeRiskBanner = () => (
  <Notice tone="info" icon={ShieldCheck} data-testid="supernova-risk-banner">
    <p className="font-semibold text-arc-accent mb-1">
      Your keys. Your custody. Always.
    </p>
    <p>
      ARC never holds your NFTs or your funds — you sign every transaction from
      your own wallet, and nothing moves without that signature. Staking is high
      risk and no outcome is promised.
    </p>
    <p className="mt-1.5">{STAKE_RISK_DISCLOSURE}</p>
    <p className="mt-1.5">{EARLY_EXIT_DISCLOSURE}</p>
  </Notice>
);

/**
 * The confirm step's custody-and-risk block — the last thing read before a
 * signature, in both modals.
 *
 * IT IS NOT A SUMMARY OF THE BANNER ABOVE IT. The banner explains staking; this
 * is the sentence the member is confirming against, at the moment their wallet
 * is about to open. Both modals render the same component so the stake and the
 * unstake confirms cannot say it differently.
 *
 * STYLING, PER ARC-39: the gold shield of the custody banners, not an amber
 * warning triangle. Amber is this app's colour for "something went wrong" and it
 * sits, on this very screen, on the failed-transaction panel — spending it on
 * the custody guarantee both dilutes the real warnings and frames sovereignty as
 * an alarm. The copy itself is CONFIRM_RISK_SENTENCE, unchanged and unsplit: one
 * element, one colour, so the risk clause is rendered at exactly the weight of
 * the custody clause it closes. Nothing here is a gradient — a gradient fill
 * computes transparent where `background-clip: text` is unsupported, which is
 * never acceptable for compliance copy.
 */
export const ConfirmRiskBlock = () => (
  <div className="sn-confirm-risk" data-testid="supernova-confirm-risk">
    <ShieldCheck size={16} className="sn-confirm-risk-ico" aria-hidden="true" />
    <p className="sn-confirm-risk-txt">{CONFIRM_RISK_SENTENCE}</p>
  </div>
);

/**
 * The tab's closing disclosure.
 *
 * The first paragraph is the v4 mockup's footer copy, VERBATIM — it is approved
 * wording and is not to be reworded, reordered, or trimmed here.
 *
 * The second paragraph is the stake-risk and early-exit copy that used to sit in
 * a banner at the top of the tab, quoted from config/supernovaXp.js. The
 * redesign moved it; it did not drop it, and it may not be dropped. The
 * third-party-contract risk and the early-exit re-rate are facts a member
 * needs, and the mockup's paragraph does not carry either.
 */
export const DisclosureFooter = () => (
  <footer className="sn-footer" data-testid="supernova-disclosure-footer">
    <p>
      Educational platform. Staking authorizes automated execution of strategies
      you select; automated trading of leveraged instruments carries substantial
      risk, including total loss. ARC is non-custodial — your wallet signs every
      transaction, ARC never holds your NFTs or your funds, and you may unstake
      at any time subject to the lock terms you chose. XP has no cash value and
      is not a security, a yield, or a promise of return. Nothing here is
      financial, investment, or tax advice. Past performance does not indicate
      future results.
    </p>
    <p>
      {STAKE_RISK_DISCLOSURE} {EARLY_EXIT_DISCLOSURE} {XP_DISCLAIMER}
    </p>
    <p className="text-[10px] font-mono uppercase tracking-[0.14em] text-arc-ink-4">
      Non-custodial · Your keys stay with you · Unstake anytime
    </p>
  </footer>
);
